import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";

// Generic add/edit dialog driven by a field schema.
// fields: [{ key, label, type: 'text'|'textarea'|'select', options?, placeholder? }]
export default function CrudDialog({ open, onOpenChange, title, fields, initial, onSubmit, testidPrefix = "crud" }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (open) {
      const base = {};
      fields.forEach((f) => (base[f.key] = initial?.[f.key] ?? ""));
      setForm(base);
    }
  }, [open, initial, fields]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0c1017] border-slate-700 text-slate-100 max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-head uppercase tracking-wider text-cyan-400 text-2xl">{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">{f.label}</Label>
              {f.type === "textarea" ? (
                <Textarea
                  data-testid={`${testidPrefix}-field-${f.key}`}
                  value={form[f.key] || ""}
                  placeholder={f.placeholder}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="bg-[#121824] border-slate-700 text-slate-100 focus-visible:ring-cyan-400 font-mono-tt text-sm"
                />
              ) : f.type === "select" ? (
                <select
                  data-testid={`${testidPrefix}-field-${f.key}`}
                  value={form[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="w-full h-10 rounded-md bg-[#121824] border border-slate-700 text-slate-100 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                >
                  <option value="">Select…</option>
                  {f.options.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              ) : (
                <Input
                  data-testid={`${testidPrefix}-field-${f.key}`}
                  value={form[f.key] || ""}
                  placeholder={f.placeholder}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="bg-[#121824] border-slate-700 text-slate-100 focus-visible:ring-cyan-400 font-mono-tt text-sm"
                />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button
            data-testid={`${testidPrefix}-cancel-btn`}
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            Cancel
          </Button>
          <Button
            data-testid={`${testidPrefix}-save-btn`}
            onClick={() => onSubmit(form)}
            className="bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
